import React, { useState, useRef, useCallback, useEffect, ReactNode } from 'react';
import {
  GripVertical,
  PanelLeftClose,
  PanelRightClose,
  PanelLeftOpen,
  PanelRightOpen,
  FileText,
  Wand2,
  Layers,
  Sparkles,
  MessageSquare,
  FolderOpen,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface File {
  id: string;
  name: string;
  type?: string;
}

interface Output {
  id: string;
  type: 'summary' | 'quiz' | 'flashcards' | 'outline' | 'custom';
}

interface ResizableThreeColumnLayoutProps {
  leftPanel: ReactNode;
  centerPanel: ReactNode;
  rightPanel: ReactNode;
  leftPanelMinWidth?: number;
  centerPanelMinWidth?: number;
  rightPanelMinWidth?: number;
  leftPanelDefaultWidth?: number;
  rightPanelDefaultWidth?: number;
  onLeftCollapseChange?: (collapsed: boolean) => void;
  onRightCollapseChange?: (collapsed: boolean) => void;
  leftCollapsedContent?: ReactNode;
  rightCollapsedContent?: ReactNode;
  files?: File[];
  outputs?: Output[];
  activeTab?: 'left' | 'center' | 'right';
  onTabChange?: (tab: 'left' | 'center' | 'right') => void;
}

export const ResizableThreeColumnLayout: React.FC<ResizableThreeColumnLayoutProps> = ({
  leftPanel,
  centerPanel,
  rightPanel,
  leftPanelMinWidth = 250,
  centerPanelMinWidth = 350,
  rightPanelMinWidth = 280,
  leftPanelDefaultWidth = 320,
  rightPanelDefaultWidth = 320,
  onLeftCollapseChange,
  onRightCollapseChange,
  leftCollapsedContent,
  rightCollapsedContent,
  files = [],
  outputs = [],
  activeTab: controlledActiveTab,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const [leftWidth, setLeftWidth] = useState(leftPanelDefaultWidth);
  const [rightWidth, setRightWidth] = useState(rightPanelDefaultWidth);
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  const [isResizingRight, setIsResizingRight] = useState(false);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [internalActiveTab, setInternalActiveTab] = useState<'left' | 'center' | 'right'>('center');
  const containerRef = useRef<HTMLDivElement>(null);

  // Use controlled activeTab if provided, otherwise use internal state
  const activeTab = controlledActiveTab ?? internalActiveTab;

  const handleTabChange = useCallback(
    (tab: 'left' | 'center' | 'right') => {
      if (onTabChange) {
        onTabChange(tab);
      } else {
        setInternalActiveTab(tab);
      }
    },
    [onTabChange]
  );

  // File type display helpers
  const getFileTypeDisplay = (fileType?: string) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return 'PDF';
      case 'docx':
      case 'doc':
        return 'DOC';
      case 'xlsx':
      case 'xls':
        return 'XLS';
      default:
        return 'FILE';
    }
  };

  const getFileTypeColor = (fileType?: string) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return 'bg-red-50 text-red-600 border-red-200';
      case 'docx':
      case 'doc':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'xlsx':
      case 'xls':
        return 'bg-green-50 text-green-600 border-green-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const getToolIcon = (type: Output['type']) => {
    switch (type) {
      case 'summary':
        return { icon: FileText, color: 'bg-blue-50 text-blue-600 border-blue-200' };
      case 'quiz':
        return { icon: Sparkles, color: 'bg-purple-50 text-purple-600 border-purple-200' };
      case 'flashcards':
        return { icon: Layers, color: 'bg-green-50 text-green-600 border-green-200' };
      case 'outline':
        return { icon: Layers, color: 'bg-orange-50 text-orange-600 border-orange-200' };
      default:
        return { icon: Wand2, color: 'bg-pink-50 text-pink-600 border-pink-200' };
    }
  };

  // All available tools
  const allTools: Array<{ id: string; type: Output['type'] }> = [
    { id: 'summary', type: 'summary' },
    { id: 'quiz', type: 'quiz' },
    { id: 'flashcards', type: 'flashcards' },
    { id: 'outline', type: 'outline' },
    { id: 'custom', type: 'custom' },
  ];

  const handleMouseDownLeft = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingLeft(true);
  }, []);

  const handleMouseDownRight = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingRight(true);
  }, []);

  const toggleLeftCollapse = useCallback(() => {
    setLeftCollapsed(prev => {
      const newValue = !prev;
      onLeftCollapseChange?.(newValue);
      return newValue;
    });
  }, [onLeftCollapseChange]);

  const toggleRightCollapse = useCallback(() => {
    setRightCollapsed(prev => {
      const newValue = !prev;
      onRightCollapseChange?.(newValue);
      return newValue;
    });
  }, [onRightCollapseChange]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current || leftCollapsed || rightCollapsed) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;

      if (isResizingLeft) {
        const newLeftWidth = e.clientX - containerRect.left;
        const maxLeftWidth = containerWidth - rightWidth - centerPanelMinWidth - 8; // 8px for resizers
        const clampedWidth = Math.max(leftPanelMinWidth, Math.min(newLeftWidth, maxLeftWidth));
        setLeftWidth(clampedWidth);
      }

      if (isResizingRight) {
        const newRightWidth = containerRect.right - e.clientX;
        const maxRightWidth = containerWidth - leftWidth - centerPanelMinWidth - 8;
        const clampedWidth = Math.max(rightPanelMinWidth, Math.min(newRightWidth, maxRightWidth));
        setRightWidth(clampedWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizingLeft(false);
      setIsResizingRight(false);
    };

    if (isResizingLeft || isResizingRight) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    isResizingLeft,
    isResizingRight,
    leftWidth,
    rightWidth,
    leftPanelMinWidth,
    rightPanelMinWidth,
    centerPanelMinWidth,
    leftCollapsed,
    rightCollapsed,
  ]);

  // Desktop Layout (lg and above)
  const desktopLayout = (
    <div ref={containerRef} className='flex h-full w-full overflow-hidden'>
      {/* Left Panel */}
      {leftCollapsed ? (
        <div className='flex-shrink-0 w-10 border-r border-gray-200 bg-white flex flex-col'>
          {/* Collapse Button at top */}
          <button
            onClick={toggleLeftCollapse}
            className='flex-shrink-0 h-10 border-b border-gray-200 hover:bg-gray-50 transition-colors flex flex-col items-center justify-center gap-0.5 group'
            title={t('common.expand')}
          >
            <FileText size={14} className='text-gray-500 group-hover:text-gray-700' />
            <PanelLeftOpen size={10} className='text-gray-400 group-hover:text-gray-600' />
          </button>

          {/* File thumbnails */}
          <div className='flex-1 overflow-y-auto py-2 space-y-1'>
            {leftCollapsedContent ||
              (files.length > 0 ? (
                <div className='flex flex-col items-center gap-2 px-1'>
                  {files.slice(0, 10).map(file => {
                    const typeDisplay = getFileTypeDisplay(file.type);
                    const typeColor = getFileTypeColor(file.type);
                    return (
                      <div
                        key={file.id}
                        className={`w-6 h-6 ${typeColor} rounded flex items-center justify-center`}
                        title={file.name}
                      >
                        <span className='text-[7px] font-bold'>{typeDisplay}</span>
                      </div>
                    );
                  })}
                  {files.length > 10 && (
                    <div className='w-6 h-6 bg-gray-100 text-gray-500 rounded flex items-center justify-center'>
                      <span className='text-[7px] font-bold'>+{files.length - 10}</span>
                    </div>
                  )}
                </div>
              ) : null)}
          </div>
        </div>
      ) : (
        <>
          <div
            style={{ width: leftWidth }}
            className='flex-shrink-0 border-r border-gray-200 bg-white overflow-hidden relative group'
          >
            {leftPanel}
            {/* Collapse Button - shows on panel hover */}
            <button
              onClick={toggleLeftCollapse}
              className='absolute top-3 right-3 p-1.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm opacity-0 group-hover:opacity-100 z-10'
              title={t('common.collapse')}
            >
              <PanelLeftClose size={14} className='text-gray-500' />
            </button>
          </div>

          {/* Left Resizer */}
          <div
            className={`w-1 flex-shrink-0 bg-gray-200 hover:bg-blue-400 cursor-col-resize transition-colors relative group ${
              isResizingLeft ? 'bg-blue-400' : ''
            }`}
            onMouseDown={handleMouseDownLeft}
          >
            <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity'>
              <GripVertical size={14} className='text-gray-500 group-hover:text-white' />
            </div>
          </div>
        </>
      )}

      {/* Center Panel */}
      <div className='flex-1 min-w-0 overflow-hidden'>{centerPanel}</div>

      {/* Right Resizer */}
      {rightCollapsed ? null : (
        <div
          className={`w-1 flex-shrink-0 bg-gray-200 hover:bg-blue-400 cursor-col-resize transition-colors relative group ${
            isResizingRight ? 'bg-blue-400' : ''
          }`}
          onMouseDown={handleMouseDownRight}
        >
          <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity'>
            <GripVertical size={14} className='text-gray-500 group-hover:text-white' />
          </div>
        </div>
      )}

      {/* Right Panel */}
      {rightCollapsed ? (
        <div className='flex-shrink-0 w-10 border-l border-gray-200 bg-white flex flex-col'>
          {/* Collapse Button at top */}
          <button
            onClick={toggleRightCollapse}
            className='flex-shrink-0 h-10 border-b border-gray-200 hover:bg-gray-50 transition-colors flex flex-col items-center justify-center gap-0.5 group'
            title={t('common.expand')}
          >
            <Wand2 size={14} className='text-gray-500 group-hover:text-gray-700' />
            <PanelRightOpen size={10} className='text-gray-400 group-hover:text-gray-600' />
          </button>

          {/* Tools and Outputs */}
          <div className='flex-1 overflow-y-auto py-2'>
            {rightCollapsedContent || (
              <div className='flex flex-col items-center gap-2 px-1'>
                {/* All tool icons */}
                {allTools.map(tool => {
                  const { icon, color } = getToolIcon(tool.type);
                  const Icon = icon;
                  return (
                    <div
                      key={tool.id}
                      className={`w-7 h-7 ${color} rounded-lg flex items-center justify-center`}
                      title={t(`knowledge.toolbox.${tool.type}`)}
                    >
                      <Icon size={14} />
                    </div>
                  );
                })}

                {/* Divider */}
                <div className='w-6 h-px bg-gray-200 my-1' />

                {/* Output thumbnails */}
                {outputs.slice(0, 8).map(output => {
                  const { icon, color } = getToolIcon(output.type);
                  const Icon = icon;
                  return (
                    <div
                      key={output.id}
                      className={`w-7 h-7 ${color} rounded-lg flex items-center justify-center`}
                      title={t(`knowledge.toolbox.${output.type}`)}
                    >
                      <Icon size={14} />
                    </div>
                  );
                })}
                {outputs.length > 8 && (
                  <div className='w-7 h-7 bg-gray-100 text-gray-500 rounded-lg flex items-center justify-center'>
                    <span className='text-[9px] font-bold'>+{outputs.length - 8}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div
          style={{ width: rightWidth }}
          className='flex-shrink-0 border-l border-gray-200 bg-white overflow-hidden relative group'
        >
          {rightPanel}
          {/* Collapse Button - shows on panel hover */}
          <button
            onClick={toggleRightCollapse}
            className='absolute top-3 left-3 p-1.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm opacity-0 group-hover:opacity-100 z-10'
            title={t('common.collapse')}
          >
            <PanelRightClose size={14} className='text-gray-500' />
          </button>
        </div>
      )}
    </div>
  );

  // Mobile Layout with Tabs
  const mobileLayout = (
    <div className='flex flex-col h-full w-full overflow-hidden'>
      {/* Tab Headers */}
      <div className='flex-shrink-0 flex border-b border-gray-200 bg-white'>
        <button
          onClick={() => handleTabChange('left')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-colors ${
            activeTab === 'left'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <FolderOpen size={18} />
          <span className='hidden sm:inline'>{t('knowledge.files.title')}</span>
        </button>
        <button
          onClick={() => handleTabChange('center')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-colors ${
            activeTab === 'center'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <MessageSquare size={18} />
          <span className='hidden sm:inline'>{t('knowledge.chat.title')}</span>
        </button>
        <button
          onClick={() => handleTabChange('right')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-colors ${
            activeTab === 'right'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <Wand2 size={18} />
          <span className='hidden sm:inline'>{t('knowledge.toolbox.title')}</span>
        </button>
      </div>

      {/* Tab Content - always render all panels to preserve state */}
      <div className='flex-1 overflow-hidden'>
        <div className={`h-full overflow-auto ${activeTab === 'left' ? '' : 'hidden'}`}>
          {leftPanel}
        </div>
        <div className={`h-full overflow-auto ${activeTab === 'center' ? '' : 'hidden'}`}>
          {centerPanel}
        </div>
        <div className={`h-full overflow-auto ${activeTab === 'right' ? '' : 'hidden'}`}>
          {rightPanel}
        </div>
      </div>
    </div>
  );

  return (
    <div className='flex h-full w-full overflow-hidden'>
      {/* Mobile Layout */}
      <div className='lg:hidden flex h-full w-full'>{mobileLayout}</div>
      {/* Desktop Layout */}
      <div className='hidden lg:flex h-full w-full'>{desktopLayout}</div>
    </div>
  );
};
